import { useRouter, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { getSubdomainOwner } from "./subdomain";
import { isOwnerPath } from "./subdomain-history";
import { getOwnerFromPath } from "./url";

export const VIEW_AS_PARAM = "view_as";
const VIEW_AS_VISITOR = "visitor";

/**
 * Whether the URL asks for the signed-in visitor preview (`?view_as=visitor`).
 *
 * Downgrade-only and frontend-only: it makes membership gates behave as if
 * the caller were a non-member (the signed-in-non-member state, which is a
 * real visitor state). It can never grant anything — the server derives
 * membership from the session and ignores this param entirely. Any other
 * value is ignored.
 */
export function parseViewAsVisitor(search: unknown): boolean {
	if (!search || typeof search !== "object") return false;
	return (search as Record<string, unknown>)[VIEW_AS_PARAM] === VIEW_AS_VISITOR;
}

export function useViewAsVisitor(): boolean {
	const search = useSearch({ strict: false });
	return parseViewAsVisitor(search);
}

let exitRequested = false;

/** Called by the banner's Exit so the next navigation may drop the param. */
export function markViewAsExit(): void {
	exitRequested = true;
}

/** Org scope of a raw pathname (subdomain wins, as in useOrgContext). */
function orgSlugForPath(pathname: string): string | null {
	// auth/oauth/account/me live outside any org, even on an org subdomain.
	if (!isOwnerPath(pathname)) return null;
	return getSubdomainOwner() ?? getOwnerFromPath(pathname);
}

/**
 * Keep `?view_as=visitor` across in-app navigation so the first clicked link
 * doesn't silently drop the preview. Installed once at the root layout; no
 * link call-site needs to know about the param.
 *
 * Re-adds on PUSH and REPLACE navigations within the same org scope (REPLACE
 * covers in-page query updates such as collection search/filters). Switching
 * orgs (or user spaces, or auth routes) exits the preview, and the banner's
 * explicit Exit is never overridden: it calls `markViewAsExit()` first.
 */
export function usePreserveViewAsVisitor(): void {
	const router = useRouter();
	const last = useRef<{ preview: boolean; org: string | null } | null>(null);

	useEffect(() => {
		const sync = (action: string) => {
			// Raw history location: `search` is the unparsed query string
			// (router.state's copy is already parsed into an object).
			const loc = router.history.location;
			const params = new URLSearchParams(loc.search);
			const preview = params.get(VIEW_AS_PARAM) === VIEW_AS_VISITOR;
			const org = orgSlugForPath(loc.pathname);
			const prev = last.current;
			last.current = { preview, org };
			const exiting = exitRequested;
			exitRequested = false;
			if (
				prev?.preview &&
				!preview &&
				!exiting &&
				(action === "PUSH" || action === "REPLACE") &&
				prev.org !== null &&
				prev.org === org
			) {
				params.set(VIEW_AS_PARAM, VIEW_AS_VISITOR);
				router.history.replace(
					`${loc.pathname}?${params.toString()}${loc.hash}`,
				);
			}
		};
		sync("INIT");
		return router.history.subscribe(({ action }) => sync(action.type));
	}, [router]);
}
